import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../common/Header";
import { Card, CardTitle, KpiCard, TableHeader, TableCell, Badge } from "../UI";
import {
  getDashboardData,
  getAllProjects,
  getAllSimulations,
  setAgency,
} from "../../requests";
import styles from "../../_css/DashBoard.module.css";
import { deleteAuthToken } from "../../utils/helper";
import {
  removeUserInformation,
  setUserInformation,
} from "../../_store/reducers/userReducer";
import { useDispatch, useSelector } from "react-redux";
import { notifyError } from "../../utils/helper";
import useRole from "../../hooks/getRole";
import { setContext } from "../../_store/reducers/contextReducer";
import { toggleRefresh } from "../../_store/reducers/refreshReducer";

/** Dashboard GET shape: { admins, brands, projects, aiSimulations } each { active, disabled, total } */
const defaultStats = null;

function statTotal(section) {
  if (section == null || typeof section !== "object") return "—";
  const t = section.total;
  if (t == null || t === "") return "—";
  const n = Number(t);
  return Number.isFinite(n) ? n : "—";
}

function statActiveLabel(section) {
  if (section == null || typeof section !== "object") return "—";
  const a = section.active;
  if (a == null || a === "") return "—";
  const n = Number(a);
  return Number.isFinite(n) ? `${n} active` : "—";
}

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatActivityTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const dDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const time = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  if (dDate.getTime() === today.getTime()) return `Today, ${time}`;
  if (dDate.getTime() === yesterday.getTime()) return `Yesterday, ${time}`;
  return (
    d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }) +
    `, ${time}`
  );
};

const SIMULATION_STATUS = {
  0: "Collecting audience data",
  1: "Audience listed",
  2: "Audience selected",
  3: "Persona generated",
  4: "Queries generated",
  5: "Simulation completed",
  6: "Analysis completed",
  10: "Simulation saved",
  90: "Simulation running",
  91: "Scheduled",
  [-1]: "Run failed",
};

const getStatusBadgeColor = (status) => {
  if (status === -1) return "#ef4444";
  if (status === 6) return "#22c55e";
  if (status === 10) return "#f59e0b";
  if (status === 90) return "#3b82f6";
  if (status === 91) return "#a855f7";
  return "#6b7280";
};

const Index = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const [stats, setStats] = useState(defaultStats);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [simulations, setSimulations] = useState([]);
  const [simulationsLoading, setSimulationsLoading] = useState(true);
  const role = useRole();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const res = await getDashboardData();
        if (res?.status < 400) {
          const data = res?.data ?? res;
          setStats(data && typeof data === "object" ? data : defaultStats);
        } else {
          setStats(defaultStats);
        }
      } catch (err) {
        if (err?.response?.status === 401) {
          deleteAuthToken();
          dispatch(removeUserInformation());
          navigate("/login");
          notifyError(
            "Session expired. Please login again." ||
              err?.response?.data?.message,
          );
        } else {
          setStats(defaultStats);
          setError(
            err?.response?.data?.message || "Failed to load dashboard stats",
          );
        }
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [user?.agencyId]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setProjectsLoading(true);
        const res = await getAllProjects({ page: 1, limit: 5 });
        if (res?.status < 400) {
          const data = res?.data?.data ?? [];
          setProjects(Array.isArray(data) ? data : []);
        }
      } catch {
        setProjects([]);
      } finally {
        setProjectsLoading(false);
      }
    };
    fetchProjects();
  }, [user?.agencyId]);

  useEffect(() => {
    const fetchSimulations = async () => {
      try {
        setSimulationsLoading(true);
        const res = await getAllSimulations({ page: 1, limit: 5 });
        if (res?.status < 400) {
          const data = res?.data?.data ?? [];
          setSimulations(Array.isArray(data) ? data : []);
        }
      } catch {
        setSimulations([]);
      } finally {
        setSimulationsLoading(false);
      }
    };
    fetchSimulations();
  }, [user?.agencyId]);

  const recentActivity = simulations.map((s) => ({
    id: s.id,
    simulationName: s.name ?? "—",
    project: s.project?.name ?? "—",
    projectId: s.project?.id ?? null,
    brand: s.project?.brand?.name ?? "—",
    brandId: s.project?.brand?.id ?? null,
    time: formatActivityTime(s.updatedAt ?? s.createdAt),
    queries: Array.isArray(s.user_queries) ? s.user_queries.length : 0,
    // result: s.status === 6 ? 'Analysis ready' : s.status === -1 ? 'Run failed' : SIMULATION_STATUS[s.status] ?? '—',
    statusLabel: SIMULATION_STATUS[s.status] ?? "Unknown",
    statusCode: s.status,
  }));

  const goToSimulationsSection = ({ projectId, brandId, brandName, projectName, simulationId, simulationName }) => {
    if (!projectId || !brandId) return;
    dispatch(
      setContext({
        brand_id: brandId,
        brand_name: brandName || "",
        project_id: projectId,
        project_name: projectName || "",
        ...(simulationId != null && {
          simulation_id: simulationId,
          simulation_name: simulationName || "",
        }),
      }),
    );
    dispatch(toggleRefresh());
    if (role === "super_admin") {
      navigate(
        `/simulations?project_id=${projectId}&brand_id=${brandId}&agency_id=${user?.agencyId}`,
      );
    } else {
      navigate(`/simulations?project_id=${projectId}&brand_id=${brandId}`);
    }
  };

  const handleProjectRowClick = (p) => {
    const brandId = p.brand?.id;
    if (!p?.id || !brandId) return;
    if (p.isActive === false) {
      notifyError("Please activate the project to view the simulations");
      return;
    }
    goToSimulationsSection({
      projectId: p.id,
      brandId,
      brandName: p.brand?.name,
      projectName: p.name,
    });
  };

  const handleActivityRowClick = (a) => {
    goToSimulationsSection({
      projectId: a.projectId,
      brandId: a.brandId,
      brandName: a.brand,
      projectName: a.project,
      simulationId: a.id,
      simulationName: a.simulationName,
    });
  };

  const super_admin_quick_Actions = [
    {
      icon: "◈",
      label: "Create New Simulation",
      desc: "Set up a new brand analysis simulation",
      color: "#ff4b4f",
      action: () => navigate("/simulations", { state: { openCreate: true } }),
    },
    {
      icon: "⚡",
      label: "Run Simulation",
      desc: "Execute queries against LLM platforms",
      color: "#c1d850",
      action: () => navigate("/simulations", { state: { openQueries: true } }),
    },
    {
      icon: "◎",
      label: "Add New Brand",
      desc: "Register a new client brand",
      color: "#c1d850",
      action: () => navigate("/brands", { state: { openAddForm: true } }),
    },
    {
      icon: "📊",
      label: "Export Reports",
      desc: "Download results as PDF or PPTX",
      color: "#999999",
      action: () => navigate("/simulations", { state: { openDownload: true } }),
    },
  ];

  const admin_quick_Actions = [
    {
      icon: "◈",
      label: "Create New Simulation",
      desc: "Set up a new brand analysis simulation",
      color: "#ff4b4f",
      action: () => navigate("/simulations", { state: { openCreate: true } }),
    },
    {
      icon: "⚡",
      label: "Run Simulation",
      desc: "Execute queries against LLM platforms",
      color: "#c1d850",
      action: () => navigate("/simulations", { state: { openQueries: true } }),
    },
    {
      icon: "◎",
      label: "Add New Brand",
      desc: "Register a new client brand",
      color: "#c1d850",
      action: () => navigate("/brands", { state: { openAddForm: true } }),
    },
    {
      icon: "📊",
      label: "Export Reports",
      desc: "Download results as PDF or PPTX",
      color: "#999999",
      action: () => navigate("/simulations", { state: { openDownload: true } }),
    },
  ];

  const user_quick_Actions = [
    {
      icon: "⚡",
      label: "Run Simulation",
      desc: "Execute queries against LLM platforms",
      color: "#c1d850",
      action: () => navigate("/simulations", { state: { openQueries: true } }),
    },
    {
      icon: "◎",
      label: "Add New Brand",
      desc: "Register a new client brand",
      color: "#c1d850",
      action: () => navigate("/brands", { state: { openAddForm: true } }),
    },
    {
      icon: "📊",
      label: "Export Reports",
      desc: "Download results as PDF or PPTX",
      color: "#999999",
      action: () => navigate("/simulations", { state: { openDownload: true } }),
    },
  ];

  const quickActions =
    role === "super_admin"
      ? super_admin_quick_Actions
      : role === "admin"
        ? admin_quick_Actions
        : user_quick_Actions;

  return (
    <div className="dm-sans-100">
      {/* <Header userType={role} headerTitle="Start" /> */}
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>Agency Dashboard</h1>
          <p className={styles.subtitle}>
            Monitor your agency's simulations and performance
          </p>
        </div>

        <div className={styles.kpiGrid}>
          <KpiCard
            label="Total members"
            value={loading ? "—" : statTotal(stats?.admins)}
            sub={loading ? "—" : statActiveLabel(stats?.admins)}
            icon="👤"
          />
          <KpiCard
            label="Total Brands"
            value={loading ? "—" : statTotal(stats?.brands)}
            sub={loading ? "—" : statActiveLabel(stats?.brands)}
            icon="◎"
          />
          <KpiCard
            label="Total Projects"
            value={loading ? "—" : statTotal(stats?.projects)}
            sub={loading ? "—" : statActiveLabel(stats?.projects)}
            icon="◈"
          />
          <KpiCard
            label="Total Simulations"
            value={loading ? "—" : statTotal(stats?.aiSimulations)}
            sub={loading ? "—" : statActiveLabel(stats?.aiSimulations)}
            icon="⚡"
          />
        </div>
        {error && (
          <p
            className={styles.subtitle}
            style={{ color: "#ff4b4f", marginTop: -8 }}
          >
            {error}
          </p>
        )}

        <div className={styles.tableCard}>
          <Card>
            <CardTitle sub="Recent projects">
              Project Performance Overview
            </CardTitle>
            <div className={styles.tableWrapper}>
              <table>
                <thead>
                  <TableHeader
                    columns={[
                      "Project",
                      "Brand",
                      "Market",
                      "Simulations",
                      "Created At",
                    ]}
                  />
                </thead>
                <tbody>
                  {projectsLoading ? (
                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          textAlign: "center",
                          padding: 16,
                          color: "#999999",
                        }}
                      >
                        Loading…
                      </td>
                    </tr>
                  ) : projects.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          textAlign: "center",
                          padding: 16,
                          color: "#999999",
                        }}
                      >
                        No projects yet
                      </td>
                    </tr>
                  ) : (
                    projects.map((p) => (
                      <tr
                        key={p.id}
                        className={styles.projectRow}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleProjectRowClick(p)}
                      >
                        <TableCell bold>{p.name}</TableCell>
                        <TableCell>{p.brand?.name ?? "—"}</TableCell>
                        <TableCell>{p.country ?? "—"}</TableCell>
                        <TableCell>{p._count?.ai_simulations ?? "—"}</TableCell>
                        <TableCell>{formatDate(p.createdAt)}</TableCell>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        <div className={styles.twoCol}>
          <Card>
            <CardTitle>Recent Simulation Activity</CardTitle>
            {simulationsLoading ? (
              <div
                style={{ padding: 16, textAlign: "center", color: "#999999" }}
              >
                Loading…
              </div>
            ) : recentActivity.length === 0 ? (
              <div
                style={{ padding: 16, textAlign: "center", color: "#999999" }}
              >
                No simulation runs yet
              </div>
            ) : (
              recentActivity.map((a) => (
                <div
                  key={a.id}
                  className={styles.activityItem}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleActivityRowClick(a)}
                >
                  <div className={styles.activityLeft}>
                    <div className={styles.activityProject}>
                      {a.simulationName}
                    </div>
                    <div className={styles.activityMeta}>
                      {a.time} · {a.queries} queries{" "}
                      {a.project ? ` · ${a.project}` : ""}{" "}
                      {a.brand ? ` · ${a.brand.toUpperCase()}` : ""}
                    </div>
                  </div>
                  <div className={styles.activityRight}>
                    {/* <span className={styles.activityResult}>{a.result}</span> */}
                    <Badge color={getStatusBadgeColor(a.statusCode)}>
                      {a.statusLabel}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </Card>

          <Card>
            <CardTitle>Quick Actions</CardTitle>
            <div className={styles.quickActionsList}>
              {quickActions.map((qa, i) => (
                <div
                  key={i}
                  className={styles.quickAction}
                  onClick={qa.action}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && qa.action()}
                  aria-label={qa.label}
                >
                  <div
                    className={styles.quickActionIcon}
                    style={{
                      background: `${qa.color}15`,
                      border: `1px solid ${qa.color}25`,
                    }}
                  >
                    {qa.icon}
                  </div>
                  <div className={styles.quickActionText}>
                    <div className={styles.quickActionLabel}>{qa.label}</div>
                    <div className={styles.quickActionDesc}>{qa.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Index;
